/**
 * R5 — NormalizationConfirmModal.jsx
 *
 * Pop-up that surfaces ambiguous parser inferences (e.g. "flangia filettata"
 * → Cieca/Piatta) and forces the operator to confirm the right value before
 * the offer moves forward.
 *
 * Triggered by useNormalizationQueue when a voce has _inference_hints.
 */
import { useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { toast } from "sonner";
import { api, formatErrorDetail } from "../lib/api";

/**
 * @param {Object} props
 * @param {boolean} props.open
 * @param {Object|null} props.hint  — single hint object
 * @param {string} props.offerId
 * @param {number} props.voceIdx
 * @param {Object} props.voce        — full voce (for context display)
 * @param {Function} props.onResolved({field, choice, updatedVoce}) → parent merges
 * @param {Function} props.onDismiss — close without resolving
 */
export default function NormalizationConfirmModal({
  open,
  hint,
  offerId,
  voceIdx,
  voce,
  onResolved,
  onDismiss,
}) {
  const [choice, setChoice] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && hint) {
      setChoice(hint.normalized_value || (hint.alternatives?.[0] ?? ""));
    }
  }, [open, hint]);

  if (!hint) return null;

  const confirm = async () => {
    if (!choice) return;
    setSaving(true);
    try {
      const res = await api.confirmInference(
        offerId,
        voceIdx,
        hint.field,
        choice
      );
      toast.success("Scelta salvata");
      onResolved?.({
        field: hint.field,
        choice,
        updatedVoce: res.voce,
      });
    } catch (e) {
      toast.error(formatErrorDetail(e));
    } finally {
      setSaving(false);
    }
  };

  const confidencePct = Math.round((hint.confidence || 0) * 100);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !saving && onDismiss?.()}>
      <DialogContent
        className="max-w-[520px]"
        data-testid="normalization-confirm-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle
              className="h-5 w-5"
              style={{ color: "#D97706" }}
            />
            Conferma interpretazione
          </DialogTitle>
          <DialogDescription>
            Il sistema non è sicuro al 100% di come interpretare questa parte
            della richiesta. Aiutaci a scegliere il valore corretto.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Context */}
          <div
            className="rounded-md px-3 py-2 text-[13px] space-y-1"
            style={{
              background: "var(--bg-subtle)",
              border: "1px solid var(--border-subtle)",
            }}
          >
            <div>
              <span className="text-[11px] uppercase tracking-wide font-medium"
                    style={{ color: "var(--text-muted)" }}>
                Testo cliente
              </span>
              <div className="font-medium" style={{ color: "var(--text-primary)" }}>
                «{hint.raw_value || voce?.sotto_tipo || "—"}»
              </div>
            </div>
            <div className="flex items-center gap-2 text-[11px]"
                 style={{ color: "var(--text-muted)" }}>
              <span>
                Voce #{(voceIdx ?? 0) + 1} · {voce?.famiglia || "?"}
              </span>
              <span
                className="px-1.5 py-0.5 rounded"
                style={{ background: "#FEF3C7", color: "#92400E" }}
                data-testid="hint-confidence"
              >
                Confidenza {confidencePct}%
              </span>
            </div>
          </div>

          {/* Reason */}
          <div className="text-[13px] leading-[20px]"
               style={{ color: "var(--text-secondary)" }}>
            {hint.reason}
          </div>

          {/* Alternatives */}
          <div className="space-y-2">
            <div className="text-[11px] uppercase tracking-wide font-medium"
                 style={{ color: "var(--text-muted)" }}>
              Scegli il valore corretto
            </div>
            <div className="flex flex-col gap-1.5">
              {(hint.alternatives || []).map((alt) => {
                const selected = alt === choice;
                return (
                  <label
                    key={alt}
                    className="flex items-center gap-2 px-3 py-2 rounded-md cursor-pointer transition-colors"
                    style={{
                      border: selected
                        ? "1px solid var(--accent-primary)"
                        : "1px solid var(--border-subtle)",
                      background: selected
                        ? "var(--accent-subtle)"
                        : "var(--bg-surface)",
                    }}
                    data-testid={`hint-alt-${alt}`}
                  >
                    <input
                      type="radio"
                      name="hint-alt"
                      value={alt}
                      checked={selected}
                      onChange={() => setChoice(alt)}
                      className="accent-[var(--accent-primary)]"
                    />
                    <span
                      className="text-[13px] flex-1"
                      style={{
                        color: selected
                          ? "var(--accent-primary)"
                          : "var(--text-primary)",
                        fontWeight: selected ? 600 : 400,
                      }}
                    >
                      {alt}
                    </span>
                    {alt === hint.normalized_value && (
                      <span
                        className="text-[10px] uppercase tracking-wide"
                        style={{ color: "var(--text-muted)" }}
                      >
                        default
                      </span>
                    )}
                  </label>
                );
              })}
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between pt-2">
            <button
              type="button"
              onClick={onDismiss}
              disabled={saving}
              className="text-[12px] px-3 h-8 rounded-md transition-colors disabled:opacity-40"
              style={{
                color: "var(--text-muted)",
                background: "transparent",
                border: "1px solid var(--border-subtle)",
              }}
              data-testid="hint-skip-btn"
            >
              Rimanda
            </button>
            <button
              type="button"
              onClick={confirm}
              disabled={saving || !choice}
              className="inline-flex items-center gap-1.5 text-[12px] px-3 h-8 rounded-md font-medium transition-colors disabled:opacity-40"
              style={{
                background: "var(--accent-primary)",
                color: "var(--text-on-accent)",
              }}
              data-testid="hint-confirm-btn"
            >
              {saving ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <CheckCircle2 className="h-3.5 w-3.5" />
              )}
              Conferma
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
